import { useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import {
  AgreementRefreshedDocument,
  PerformanceProcessAgreementDocumentsDocument,
  PerformanceProcessAgreementDocumentsEsignaturesDocument,
  PerformanceProcessAgreementPageDocument,
  PerformanceProcessAgreementQuestionnairesDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ActionItemList } from 'modules/performance_process/components/agreements/ActionItemList'
import { AgreementCourse } from 'modules/performance_process/components/agreements/AgreementCourse'
import { AgreementConclusions } from 'modules/performance_process/components/agreements/Conclusions'
import { AgreementDocumentCard } from 'modules/performance_process/components/agreements/DocumentCard'
import { SectionableQuestions } from 'modules/performance_process/components/agreements/SectionableQuestions'

import { useStatusAlert } from './useStatusAlert'

const ReviewAgreementPage = () => {
  const { agreementId } = useParams()

  if (!agreementId) {
    throw new Error('Agreement ID is required')
  }

  const { data, loading, error, refetch } = useQuery(PerformanceProcessAgreementPageDocument, {
    variables: { id: agreementId },
  })

  const {
    data: questionnairesData,
    loading: questionnairesLoading,
    error: questionnairesError,
    refetch: refetchQuestionnaires,
  } = useQuery(PerformanceProcessAgreementQuestionnairesDocument, {
    variables: { id: agreementId },
  })

  const {
    data: documentsData,
    loading: documentsLoading,
    error: documentsError,
    refetch: refetchDocuments,
  } = useQuery(PerformanceProcessAgreementDocumentsDocument, {
    variables: { id: agreementId },
  })

  const agreement = data?.performance?.agreement
  const agreementWithQuestionnaires = questionnairesData?.performance?.agreement
  const agreementWithDocuments = documentsData?.performance?.agreement

  const agreementDocument = agreementWithDocuments?.agreementDocuments[0]
  const generatingDocument = agreementWithDocuments?.generatingDocument ?? false
  const canGenerateDocument = agreementWithDocuments?._policies?.generateDocument?.can ?? false

  const document = agreementDocument?.document ?? null
  const actionItems = agreement?.actionItemsInfo ?? []

  const canSeeDocumentCard = agreementDocument || generatingDocument || canGenerateDocument
  const canSeeActionItems = agreement?._policies?.addActionItems.can || actionItems.length > 0

  const { data: esignaturesData } = useQuery(
    PerformanceProcessAgreementDocumentsEsignaturesDocument,
    {
      variables: { documentId: Number(document?.id) },
      skip: !document?.id,
    }
  )
  const esignatures = esignaturesData?.esignature.signaturesConnection.nodes ?? []

  const statusAlert = useStatusAlert({ agreement: agreementWithDocuments ?? null, esignatures })

  useSubscription(AgreementRefreshedDocument, {
    onData: ({ data: subscriptionData }) => {
      if (subscriptionData?.data?.performanceAgreementRefresh?.agreement?.id !== agreementId) return

      refetch()
      refetchQuestionnaires()
      refetchDocuments()
    },
  })

  return (
    <ResponsivePage.Body
      loading={loading || questionnairesLoading || documentsLoading}
      error={error || questionnairesError || documentsError}
    >
      {agreement && agreementWithQuestionnaires && agreementWithDocuments && (
        <>
          {(statusAlert || canSeeDocumentCard) && (
            <Box paddingBottom={statusAlert ? 's32' : 's12'}>
              {canSeeDocumentCard && (
                <Box paddingX="s24">
                  <AgreementDocumentCard
                    agreement={agreementWithDocuments}
                    esignatures={esignatures}
                  />
                </Box>
              )}
              {statusAlert && <F0Alert {...statusAlert} />}
            </Box>
          )}

          <Box gap="s24">
            <SectionableQuestions agreement={agreementWithQuestionnaires} />

            <Box gap="s24">
              {canSeeActionItems && (
                <>
                  <Text.Header2 weight="semibold">
                    {i18n.t('performance_process.agreements.action_items.title')}
                  </Text.Header2>
                  <ActionItemList agreement={agreement} />
                </>
              )}

              <AgreementCourse agreement={agreement} HeaderComponent={Text.Header2} />

              {(!agreement.locked || agreement.conclusions) && (
                <>
                  <Text.Header2 weight="semibold">
                    {i18n.t('performance_process.agreements.conclusions.title')}
                  </Text.Header2>
                  <AgreementConclusions agreement={agreement} />
                </>
              )}
            </Box>
          </Box>
        </>
      )}
    </ResponsivePage.Body>
  )
}

export default ReviewAgreementPage
